import { useState } from 'react';
import { Field } from '../api';
import { http } from '../api';
import { AuditEvent } from './seachpage-types';

export interface UseDocumentActionsProps {
  fields: Field[];
  onDocumentUpdate?: (docId: number, updatedDoc: any) => void;
  onDocumentDelete?: (docId: number) => void;
}

export interface UseDocumentActionsReturn {
  // Edit Document
  editDoc: any | null;
  editValues: Record<string, any>;
  savingEdit: boolean;
  editError: string | null;
  openEdit: (doc: any) => void;
  saveEdit: () => Promise<void>;
  cancelEdit: () => void;
  setEditValues: (values: Record<string, any>) => void;
  
  // Delete Document
  deleteDocument: (docId: number) => Promise<void>;
  
  // Audit
  auditDoc: any | null;
  audits: AuditEvent[];
  loadingAudits: boolean;
  auditError: string | null;
  openAudit: (doc: any) => void;
  closeAudit: () => void;

  // Metadata (JArray of rows)
  metadataOpen: boolean;
  metadataRows: Array<Record<string, any>>;
  openMetadata: (doc: any) => void;
  closeMetadata: () => void;
}

export const useDocumentActions = ({ 
  fields, 
  onDocumentUpdate,
  onDocumentDelete
}: UseDocumentActionsProps): UseDocumentActionsReturn => {
  
  // Edit Document State
  const [editDoc, setEditDoc] = useState<any | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  
  // Audit State
  const [auditDoc, setAuditDoc] = useState<any | null>(null);
  const [audits, setAudits] = useState<AuditEvent[]>([]);
  const [loadingAudits, setLoadingAudits] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  
  // Metadata State (array of rows)
  const [metadataOpen, setMetadataOpen] = useState<boolean>(false);
  const [metadataRows, setMetadataRows] = useState<Array<Record<string, any>>>([]);

  // Helper function to get field by name
  function getFieldByName(name: string): Field | undefined {
    return fields.find(f => f.name === name);
  }

  // Edit Document Functions
  function openEdit(doc: any) {
    const prepared: Record<string, any> = {};
    if (doc.documentValues) {
      Object.entries(doc.documentValues).forEach(([k, v]: any) => {
        if (v == null) prepared[k] = '';
        else if (typeof v === 'object') {
          try { prepared[k] = JSON.stringify(v); } catch { prepared[k] = String(v); }
        } else prepared[k] = String(v);
      });
    }
    prepared.description = doc.documentDescription || doc.documentdescription || '';
    prepared.documentTypeId = (doc.documentTypeId ?? doc.DocumentTypeId ?? doc.documenttypeid ?? '') as any;
    setEditDoc(doc);
    setEditValues(prepared);
    setEditError(null);
  }

  async function saveEdit() {
    if (!editDoc) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = { ...editDoc };
      const { description, documentTypeId, ...rest } = editValues;
      updated.documentDescription = description;
      if (documentTypeId !== undefined && documentTypeId !== '' && documentTypeId != null) {
        const n = Number(documentTypeId);
        if (Number.isFinite(n) && n > 0) updated.documentTypeId = n;
      }
      const converted: Record<string, any> = {};
      const looksJson = (s: string) => (s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'));
      
      Object.entries(rest).forEach(([k, v]) => {
        let val: any = v;
        if (typeof val === 'string') {
          const fld = getFieldByName(k);
          const dt = fld?.datatype?.toLowerCase();
          if (val.trim() === '') val = '';
          else if (['int', 'integer', 'number'].includes(dt || '')) {
            const n = Number(val); val = isNaN(n) ? '' : n;
          } else if (['float', 'double', 'decimal', 'currency'].includes(dt || '')) {
            const n = Number(val); val = isNaN(n) ? '' : n;
          } else if (dt === 'bool' || dt === 'boolean') {
            if (val.toLowerCase() === 'true') val = true;
            else if (val.toLowerCase() === 'false') val = false;
          } else if (looksJson(val)) {
            try { val = JSON.parse(val); } catch { }
          }
        }
        converted[k] = val;
      });
      
      updated.documentValues = converted;
      await http(`/api/document/${editDoc.id}`, { method: 'PUT', json: updated });
      
      // Notify parent of document update
      if (onDocumentUpdate) {
        onDocumentUpdate(editDoc.id, {
          documentValues: updated.documentValues,
          documentDescription: updated.documentDescription,
          documentTypeId: updated.documentTypeId
        });
      }
      
      setEditDoc(null);
    } catch (e: any) {
      setEditError(e.message);
    } finally {
      setSavingEdit(false);
    }
  }

  function cancelEdit() {
    setEditDoc(null);
    setEditValues({});
    setEditError(null);
  }

  // Delete Document Function
  async function deleteDocument(docId: number) {
    await http(`/api/document/${docId}`, { method: 'DELETE' });
    if (onDocumentDelete) onDocumentDelete(docId);
  }

  // Audit Functions
  async function openAudit(doc: any) {
    setAuditDoc(doc);
    setAudits([]);
    setAuditError(null);
    setLoadingAudits(true);
    try {
      const data = await http<any[]>(`/api/documentaudit/bydocument/${doc.id}`);
      const events: AuditEvent[] = (data || []).flatMap((a: any) => (a.events || a.Events || [])).map((e: any) => ({
        text: e.text || e.Text,
        name: e.name || e.Name,
        date: e.date || e.Date
      }));
      events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setAudits(events);
    } catch (e: any) {
      setAuditError(e.message);
    } finally {
      setLoadingAudits(false);
    }
  }

  function closeAudit() {
    setAuditDoc(null);
    setAudits([]);
    setAuditError(null);
  }

  // Metadata Functions (no fallback to document fields)
  async function openMetadata(doc: any) {
    setMetadataOpen(true);
    setMetadataRows([]);
    try {
      const data = await http<any>(`/api/document/DocumentRoot/Metadata?documentId=${doc.id}`);
      if (Array.isArray(data)) {
        setMetadataRows(data as Array<Record<string, any>>);
      } else if (data && typeof data === 'object') {
        setMetadataRows([data as Record<string, any>]);
      } else {
        setMetadataRows([]);
      }
    } catch {
      setMetadataRows([]);
    }
  }

  function closeMetadata() {
    setMetadataOpen(false);
    setMetadataRows([]);
  }

  return {
    // Edit Document
    editDoc,
    editValues,
    savingEdit,
    editError,
    openEdit,
    saveEdit,
    cancelEdit,
    setEditValues,
    
    // Delete Document
    deleteDocument,
    
    // Audit
    auditDoc,
    audits,
    loadingAudits,
    auditError,
    openAudit,
    closeAudit,
    
    // Metadata (array)
    metadataOpen,
    metadataRows,
    openMetadata,
    closeMetadata
  };
};